<template>
  <div class="change-email-container">
    <div class="change-email-box">
      <div class="change-email-header">
        <h2>修改邮箱</h2>
        <p>通过邮箱验证码修改您的邮箱地址</p>
      </div>

      <!-- 步骤指示器 -->
      <div class="step-indicator">
        <div class="step" :class="{ active: currentStep >= 1, completed: currentStep > 1 }">
          <div class="step-number">1</div>
          <div class="step-text">输入新邮箱</div>
        </div>
        <div class="step-line" :class="{ active: currentStep > 1 }"></div>
        <div class="step" :class="{ active: currentStep >= 2, completed: currentStep > 2 }">
          <div class="step-number">2</div>
          <div class="step-text">验证码验证</div>
        </div>
        <div class="step-line" :class="{ active: currentStep > 2 }"></div>
        <div class="step" :class="{ active: currentStep >= 3 }">
          <div class="step-number">3</div>
          <div class="step-text">完成修改</div>
        </div>
      </div>

      <!-- 步骤1: 输入新邮箱 -->
      <div v-if="currentStep === 1" class="step-content">
        <div class="current-email-info">
          <i class="el-icon-message"></i>
          <span>当前邮箱：{{ currentEmail }}</span>
        </div>
        
        <el-form ref="emailForm" :model="emailForm" :rules="emailRules" class="change-form">
          <el-form-item prop="newEmail">
            <el-input
              v-model="emailForm.newEmail"
              placeholder="请输入新的邮箱地址"
              prefix-icon="el-icon-message"
              size="large"
              @keyup.enter.native="sendVerificationCode"
            />
          </el-form-item>
          
          <el-button 
            type="primary" 
            :loading="sendingCode" 
            class="action-btn"
            @click="sendVerificationCode"
          >
            {{ sendingCode ? '发送中...' : '发送验证码' }}
          </el-button>
        </el-form>
      </div>

      <!-- 步骤2: 验证码验证 -->
      <div v-if="currentStep === 2" class="step-content">
        <div class="email-info">
          <i class="el-icon-message"></i>
          <span>验证码已发送至：{{ emailForm.newEmail }}</span>
        </div>
        
        <el-form ref="codeForm" :model="codeForm" :rules="codeRules" class="change-form">
          <el-form-item prop="code">
            <el-input
              v-model="codeForm.code"
              placeholder="请输入6位验证码"
              prefix-icon="el-icon-key"
              size="large"
              maxlength="6"
              @keyup.enter.native="verifyCode"
            />
          </el-form-item>
          
          <div class="code-actions">
            <el-button 
              type="primary" 
              :loading="verifyingCode" 
              class="action-btn"
              @click="verifyCode"
            >
              {{ verifyingCode ? '验证中...' : '验证码验证' }}
            </el-button>
            
            <el-button 
              type="text" 
              :disabled="countdown > 0"
              @click="resendCode"
            >
              {{ countdown > 0 ? `${countdown}秒后重发` : '重新发送' }}
            </el-button>
          </div>
        </el-form>
      </div>

      <!-- 步骤3: 完成修改 -->
      <div v-if="currentStep === 3" class="step-content">
        <div class="success-info">
          <i class="el-icon-success"></i>
          <span>验证码验证成功</span>
        </div>
        
        <div class="confirm-section">
          <div class="email-change-info">
            <div class="email-item">
              <span class="label">原邮箱：</span>
              <span class="value">{{ currentEmail }}</span>
            </div>
            <div class="email-item">
              <span class="label">新邮箱：</span>
              <span class="value new-email">{{ emailForm.newEmail }}</span>
            </div>
          </div>
          
          <el-button 
            type="primary" 
            :loading="changingEmail" 
            class="action-btn"
            @click="changeEmail"
          >
            {{ changingEmail ? '修改中...' : '确认修改邮箱' }}
          </el-button>
        </div>
      </div>

      <!-- 返回按钮 -->
      <div class="back-to-details">
        <router-link to="/user/details">
          <i class="el-icon-arrow-left"></i>
          返回个人信息
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { sendEmailChangeCode, verifyEmailChangeCode, changeEmail } from '@/api/email'
import { mapGetters } from 'vuex'

export default {
  name: 'ChangeEmail',
  data() {
    return {
      currentStep: 1,
      sendingCode: false,
      verifyingCode: false,
      changingEmail: false,
      countdown: 0,
      countdownTimer: null,
      
      emailForm: {
        newEmail: ''
      },
      
      codeForm: {
        code: ''
      },
      
      emailRules: {
        newEmail: [
          { required: true, message: '请输入新邮箱地址', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ]
      },
      
      codeRules: {
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { min: 6, max: 6, message: '验证码为6位数字', trigger: 'blur' }
        ]
      }
    }
  },
  
  computed: {
    ...mapGetters(['email']),
    currentEmail() {
      return this.email || '加载中...'
    }
  },
  
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  
  methods: {
    // 发送验证码
    async sendVerificationCode() {
      try {
        await this.$refs.emailForm.validate()
        this.sendingCode = true
        
        await sendEmailChangeCode(this.emailForm.newEmail)
        this.$message.success('验证码已发送到新邮箱，请查收')
        this.currentStep = 2
        this.startCountdown()
      } catch (error) {
        // 错误由全局拦截器处理
      } finally {
        this.sendingCode = false
      }
    },
    
    // 验证验证码
    async verifyCode() {
      try {
        await this.$refs.codeForm.validate()
        this.verifyingCode = true
        
        await verifyEmailChangeCode(this.emailForm.newEmail, this.codeForm.code)
        this.$message.success('验证码验证成功')
        this.currentStep = 3
      } catch (error) {
        // 错误由全局拦截器处理
      } finally {
        this.verifyingCode = false
      }
    },
    
    // 修改邮箱
    async changeEmail() {
      try {
        this.changingEmail = true
        
        await changeEmail(this.emailForm.newEmail, this.codeForm.code)
        
        this.$message.success('邮箱修改成功')
        // 更新store中的邮箱信息
        this.$store.dispatch('updateUserEmail', this.emailForm.newEmail)
        // 延迟跳转，让用户看到成功消息
        setTimeout(() => {
          this.$router.push('/user/details')
        }, 1500)
      } catch (error) {
        // 错误由全局拦截器处理
      } finally {
        this.changingEmail = false
      }
    },
    
    // 重新发送验证码
    async resendCode() {
      if (this.countdown > 0) return
      
      try {
        this.sendingCode = true
        await sendEmailChangeCode(this.emailForm.newEmail)
        this.$message.success('验证码已重新发送')
        this.startCountdown()
      } catch (error) {
        // 错误由全局拦截器处理
      } finally {
        this.sendingCode = false
      }
    },
    
    // 开始倒计时
    startCountdown() {
      this.countdown = 60
      this.countdownTimer = setInterval(() => {
        this.countdown--
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
        }
      }, 1000)
    }
  }
}
</script>

<style scoped>
.change-email-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #66eae3 0%, #ffc7ff 100%);
  padding: 20px;
}

.change-email-box {
  width: 100%;
  max-width: 500px;
  padding: 40px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.change-email-header {
  text-align: center;
  margin-bottom: 40px;
}

.change-email-header h2 {
  color: #333;
  margin-bottom: 10px;
  font-size: 28px;
  font-weight: 600;
}

.change-email-header p {
  color: #666;
  font-size: 14px;
}

/* 步骤指示器 */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  padding: 0 20px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e0e0e0;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #409eff;
  color: white;
}

.step.completed .step-number {
  background: #67c23a;
  color: white;
}

.step-text {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
  text-align: center;
}

.step.active .step-text {
  color: #409eff;
  font-weight: 500;
}

.step.completed .step-text {
  color: #67c23a;
}

.step-line {
  width: 60px;
  height: 2px;
  background: #e0e0e0;
  margin: 0 10px;
  transition: all 0.3s ease;
}

.step-line.active {
  background: #409eff;
}

/* 步骤内容 */
.step-content {
  margin-bottom: 30px;
}

.change-form {
  margin-top: 20px;
}

.action-btn {
  width: 100%;
  height: 48px;
  font-size: 16px;
  border-radius: 8px;
  margin-top: 20px;
}

.current-email-info, .email-info, .success-info {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  margin-bottom: 20px;
  color: #409eff;
  font-size: 14px;
}

.current-email-info i, .email-info i, .success-info i {
  margin-right: 8px;
  font-size: 16px;
}

.success-info {
  background: #f0f9ff;
  border-color: #67c23a;
  color: #67c23a;
}

.code-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.code-actions .action-btn {
  margin-top: 0;
}

/* 确认修改部分 */
.confirm-section {
  margin-top: 20px;
}

.email-change-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.email-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.email-item:last-child {
  margin-bottom: 0;
}

.email-item .label {
  color: #666;
  font-weight: 500;
  width: 80px;
  flex-shrink: 0;
}

.email-item .value {
  color: #333;
  font-weight: 500;
}

.email-item .value.new-email {
  color: #409eff;
  font-weight: 600;
}

.back-to-details {
  text-align: center;
  margin-top: 30px;
}

.back-to-details a {
  color: #666;
  text-decoration: none;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  transition: color 0.3s ease;
}

.back-to-details a:hover {
  color: #409eff;
}

.back-to-details i {
  margin-right: 4px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .change-email-container {
    padding: 10px;
  }
  
  .change-email-box {
    padding: 30px 20px;
  }
  
  .step-indicator {
    padding: 0 10px;
  }
  
  .step-line {
    width: 40px;
    margin: 0 5px;
  }
  
  .step-number {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  
  .step-text {
    font-size: 11px;
  }
}

@media (max-width: 480px) {
  .change-email-box {
    padding: 20px 15px;
  }
  
  .change-email-header h2 {
    font-size: 24px;
  }
  
  .step-indicator {
    flex-wrap: wrap;
    gap: 10px;
  }
  
  .step-line {
    display: none;
  }
  
  .email-change-info {
    padding: 15px;
  }
  
  .email-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  
  .email-item .label {
    width: auto;
  }
}
</style>
